import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import type {BaseSyntheticEvent, FC, ReactElement, SyntheticEvent} from 'react'
import {StrictMode, useState} from 'react'
import {useSelector} from 'react-redux'
import type {RootState} from '@containers/App/store'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useTimeNavigation} from '@hooks/useTimeNavigation'
import type {TimeRangeInterface} from '@interfaces/timerange'
import CustomDatePicker from '@shared/components/timerange/CustomDatePicker'
import RollingRangeFields from '@shared/components/timerange/RollingRangeFields'
import {
  buildRollingRangeId,
  MAX_ROLLING_RANGE_COUNT,
  parseRollingRange,
  PREVIOUS_SHIFT_RANGE_ID,
  type RollingRangeUnit,
} from '@shared/utils/timeRange'
import {
  type TimeIntervalContextProps,
  useTimeIntervalContext,
} from '@shared/providers/TimeIntervalProvider'

type Props = {
  timeRangeOptions: TimeRangeInterface[]
  page?: string
  // The window step-back/forward paging is part of this control (one bordered
  // group). Pages that load data on the window pass their fetch state so the
  // arrows grey out mid-fetch / on error / when empty, exactly as the old
  // standalone TimeNavigationControls did.
  navHasError?: boolean
  navIsLoading?: boolean
  navHasData?: boolean
  // Force the whole control disabled (on top of the timemode gate) with a reason
  // shown on hover — e.g. the signals overlay's Last-N rule ignores the page
  // window entirely, so the picker would be a no-op.
  disabled?: boolean
  disabledTooltip?: string
  // Offer "last N hours / days" with a free N (AB#6936). Only pages whose time
  // provider is all that reads the range opt in (the dashboards).
  allowRollingRange?: boolean
}
const DateIntervalPicker: FC<Props> = ({
  timeRangeOptions,
  page,
  navHasError = false,
  navIsLoading = false,
  navHasData = true,
  disabled = false,
  disabledTooltip,
  allowRollingRange = false,
}): ReactElement => {
  const timeMode = useSelector((state: RootState) => state.timemode.value)

  // Hooks
  const [t2] = useCustomTranslation('timerange')
  const {updateTimeInterval, timeRange} = useTimeIntervalContext() as TimeIntervalContextProps
  const {handleTimeNavigation, isForwardDisabled, isBackwardDisabled} = useTimeNavigation({
    hasError: navHasError,
    isLoading: navIsLoading,
    hasData: navHasData,
  })

  // States
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const activeRolling = parseRollingRange(timeRange)
  const [rollingOpen, setRollingOpen] = useState(false)
  const [rollingCount, setRollingCount] = useState<number | null>(activeRolling?.count ?? 36)
  const [rollingUnit, setRollingUnit] = useState<RollingRangeUnit>(activeRolling?.unit ?? 'hours')
  const rollingValid =
    rollingCount !== null &&
    rollingCount >= 1 &&
    rollingCount <= MAX_ROLLING_RANGE_COUNT[rollingUnit]

  // Functions
  const handleClick = (event: BaseSyntheticEvent): void => {
    setAnchorEl(event.currentTarget)
    // Reopening shows the range in effect, not a half-typed one.
    setRollingOpen(Boolean(activeRolling))
    if (activeRolling) {
      setRollingCount(activeRolling.count)
      setRollingUnit(activeRolling.unit)
    }
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const applyRollingRange = (): void => {
    if (!rollingValid || rollingCount === null) return
    updateTimeInterval(buildRollingRangeId({count: rollingCount, unit: rollingUnit}))
    handleClose()
  }

  const handleTimeRangeChange = (_: SyntheticEvent, item: TimeRangeInterface): void => {
    updateTimeInterval(item.value)
    handleClose()
  }

  // Find the current timeRangeObject directly in the render
  const timeRangeObject =
    timeRangeOptions.find((item) => item.value === timeRange) ||
    timeRangeOptions.find((item) => item.value === 'null') ||
    timeRangeOptions[0]
  // "Previous shift" sits with "shift so far": both are aligned to the shifts.
  const isShiftAligned = (item: TimeRangeInterface): boolean =>
    item.value.includes('so-far') ||
    item.name.includes('so_far') ||
    item.value === PREVIOUS_SHIFT_RANGE_ID
  const soFarOptions = timeRangeOptions.filter(isShiftAligned)
  const customOption = timeRangeOptions.find((item) => item.value === 'null')
  const regularOptions = timeRangeOptions.filter(
    (item) => item.value !== 'null' && !isShiftAligned(item),
  )
  const buttonLabel = activeRolling
    ? t2(`last_n_${activeRolling.unit}`, {count: activeRolling.count})
    : t2(timeRangeObject?.name)
  const shiftTooltip = t2('shift_so_far_tooltip', {
    times: (() => {
      const formatter = new Intl.DateTimeFormat(undefined, {hour: 'numeric', minute: '2-digit'})
      const now = new Date()
      const formatHour = (hour: number): string => {
        const date = new Date(now)
        date.setHours(hour, 0, 0, 0)
        return formatter.format(date)
      }
      return [formatHour(6), formatHour(14), formatHour(22)].join(', ')
    })(),
  })

  const open = Boolean(anchorEl)

  const groupDisabled = Boolean((!timeMode && page === 'signals') || disabled)

  return (
    <StrictMode>
      {/* A disabled ButtonGroup swallows pointer events, so the reason lives on a
          wrapper span the Tooltip can still hover. Empty title = no tooltip. */}
      <Tooltip title={disabled && disabledTooltip ? disabledTooltip : ''} arrow>
        <Box component="span" sx={{display: 'inline-flex'}}>
          <ButtonGroup
            variant="outlined"
            size="small"
            color="inherit"
            // Subtle toolbar look: muted text, hairline divider borders.
            sx={{color: 'text.secondary', '& .MuiButton-root': {borderColor: 'divider'}}}
            disabled={groupDisabled}
            id="signal-screen-date-interval-picker-button-group"
            data-testid="signal-screen-date-interval-picker-button-group"
            data-cy="signal-screen-date-interval-picker-button-group"
          >
            <Button
              id="signal-screen-open-default-timerange-selector"
              data-testid="signal-screen-open-default-timerange-selector"
              data-cy="signal-screen-open-default-timerange-selector"
              value={timeRangeObject?.value}
              onClick={handleClick}
              startIcon={<CalendarMonthIcon fontSize="small" />}
              endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {buttonLabel}
            </Button>
            <CustomDatePicker />
            <Button
              onClick={() => handleTimeNavigation('back')}
              disabled={isBackwardDisabled}
              aria-label={t2('navigate_backward')}
              data-testid="time-navigate-back"
              sx={{px: 1, minWidth: 0}}
            >
              <ArrowBackIcon fontSize="small" />
            </Button>
            <Button
              onClick={() => handleTimeNavigation('forward')}
              disabled={isForwardDisabled}
              aria-label={t2('navigate_forward')}
              data-testid="time-navigate-forward"
              sx={{px: 1, minWidth: 0}}
            >
              <ArrowForwardIcon fontSize="small" />
            </Button>
          </ButtonGroup>
        </Box>
      </Tooltip>
      <Menu
        id="signal-screen-date-interval-picker-popover"
        data-testid="signal-screen-date-interval-picker-popover"
        data-cy="signal-screen-date-interval-picker-popover"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{'aria-labelledby': 'basic-button'}}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {regularOptions.map((item: TimeRangeInterface) => (
          <MenuItem
            className={item.value === timeRange ? 'Mui-selected' : ''}
            onClick={(e) => handleTimeRangeChange(e, item)}
            key={item.id}
            id={`signal-screen-timerange-value-${item.value}`}
            data-testid={item.value}
            data-cy="mbai-date-interval-picker-menu-item"
          >
            <ListItemText>{t2(item.name)}</ListItemText>
          </MenuItem>
        ))}
        {soFarOptions.length > 0 ? <Divider sx={{my: 0.5}} /> : null}
        {soFarOptions.map((item: TimeRangeInterface) => (
          <MenuItem
            className={item.value === timeRange ? 'Mui-selected' : ''}
            onClick={(e) => handleTimeRangeChange(e, item)}
            key={item.id}
            id={`signal-screen-timerange-value-${item.value}`}
            data-testid={item.value}
            data-cy="mbai-date-interval-picker-menu-item"
          >
            {item.value === 'shift-so-far' ||
            item.name === 'shift_so_far' ||
            item.value === PREVIOUS_SHIFT_RANGE_ID ? (
              <Tooltip title={shiftTooltip} placement="right" arrow>
                <Box component="span">
                  <ListItemText>{t2(item.name)}</ListItemText>
                </Box>
              </Tooltip>
            ) : (
              <ListItemText>{t2(item.name)}</ListItemText>
            )}
          </MenuItem>
        ))}
        {allowRollingRange ? <Divider sx={{my: 0.5}} /> : null}
        {allowRollingRange ? (
          <MenuItem
            className={activeRolling ? 'Mui-selected' : ''}
            onClick={() => setRollingOpen((open) => !open)}
            id="signal-screen-timerange-value-rolling"
            data-testid="rolling"
            data-cy="mbai-date-interval-picker-rolling-range"
          >
            <ListItemText>{t2('last_n')}</ListItemText>
            {rollingOpen ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </MenuItem>
        ) : null}
        {allowRollingRange ? (
          <Collapse in={rollingOpen} unmountOnExit>
            <Box
              sx={{px: 2, pt: 1, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1}}
              // Keep the menu's type-ahead and arrow keys out of the fields.
              onKeyDown={(event) => {
                event.stopPropagation()
                if (event.key === 'Enter') applyRollingRange()
              }}
            >
              <RollingRangeFields
                count={rollingCount}
                unit={rollingUnit}
                onCountChange={setRollingCount}
                onUnitChange={setRollingUnit}
                dataCyPrefix="dashboard"
                autoFocus
              />
              <Button
                variant="contained"
                size="small"
                disabled={!rollingValid}
                onClick={applyRollingRange}
                data-cy="dashboard-rolling-range-apply"
                sx={{alignSelf: 'flex-end'}}
              >
                {t2('apply')}
              </Button>
            </Box>
          </Collapse>
        ) : null}
        {customOption ? <Divider sx={{my: 0.5}} /> : null}
        {customOption ? (
          <MenuItem
            className={customOption.value === timeRange ? 'Mui-selected' : ''}
            onClick={(e) => handleTimeRangeChange(e, customOption)}
            key={customOption.id}
            id={`signal-screen-timerange-value-${customOption.value}`}
            data-testid={customOption.value}
            data-cy="mbai-date-interval-picker-menu-item"
          >
            <ListItemText>{t2(customOption.name)}</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>
    </StrictMode>
  )
}
export default DateIntervalPicker
